import {
  Component,
  AfterViewInit,
  OnInit,
  OnDestroy,
  Inject,
  Input,
  ViewChild,
  ElementRef,
  ViewEncapsulation,
} from "@angular/core";
import { IUProperty } from "src/app/shared/interface/umbraco-property";
import { HttpClient } from "@angular/common/http";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { Subject, timer } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { RecaptchaComponent } from "ng-recaptcha";
import { ICapitalizeTitle } from "src/app/shared/interface/capitalize-title";
import { IModalVideo } from "src/app/shared/components/modal-video/modal-video.component";
import { IVideoPickerData } from "src/app/shared/interface/video-picker-data";
import { applyTitleSettings } from "../../shared/utils/title-style-settings-utils";
import { ITitleStyleSettings } from "../../shared/interface/title-style-settings";
import { processContent } from "../../shared/utils/rotating-words-utils";
import { IRotatingWords } from "../../shared/interface/rotating-words";
import { IRteStyleSettings } from "../../shared/interface/rte-style-settings";
import { applyRteSettings } from "../../shared/utils/rte-style-settings-utils";
import {
  IScopedCssController,
  ScopedPanelCssService,
} from "src/app/shared/utils/scoped-panel-css.service";

interface IButton {
  name: string;
  target?: string;
  type: ButtonType;
  url: string;
}

enum ButtonType {
  link = 0,
  media = 1,
  external = 2,
}

type CurveType = "None" | "Wave" | "Curve";

interface IBoardHeroDemoFormPanelData extends ICapitalizeTitle {
  title?: IUProperty<string>;
  description?: IUProperty<string>;
  customCss?: IUProperty<string>;
  formTitle?: IUProperty<string>;
  rotatingWords?: IUProperty<IRotatingWords[]>;
  fontSize: IUProperty<string>;
  formButtonText: IUProperty<string>;
  formHidden: IUProperty<boolean>;
  buttons: IUProperty<IBoardHeroDemoFormButtonItemViewModel[]>;
  buttonsColor: IUProperty<string>;
  mailType?: IUProperty<number>;
  items?: any[];
  itemsTitle: IUProperty<string>;
  backgroundMedia: IUProperty<any>;
  removeBackgroundOverlay: IUProperty<boolean>;
  capitalizeTitle: IUProperty<boolean>;
  cookiePolicyPageUrl: string;
  reCaptchaSecret: string;
  curve: IUProperty<CurveType>;
  overlayColor: IUProperty<string>;
  overlayPercent: IUProperty<number>;

  rteStyleSettings?: IRteStyleSettings;
  titleStyleSettings?: ITitleStyleSettings;
}

interface IBoardHeroDemoFormPanelResponseData {
  reCaptchaFailed: boolean;
}

interface IBoardHeroDemoFormButtonItemViewModel {
  button: IUProperty<IButton>;
  renderAsLink: IUProperty<boolean>;
  isModalShow?: boolean;
}

@Component({
  selector: "app-board-hero-demo-form-panel",
  templateUrl: "./board-hero-demo-form-panel.component.html",
  styleUrls: ["./board-hero-demo-form-panel.component.less"],
  encapsulation: ViewEncapsulation.None,
})
export class BoardHeroDemoFormPanelComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() data: IBoardHeroDemoFormPanelData;

  @ViewChild("captcha", { static: false }) captcha: RecaptchaComponent;
  @ViewChild("titleEl", { static: false }) titleEl: ElementRef<HTMLElement>;
  @ViewChild("panelEl", { static: false }) panelEl: ElementRef<HTMLElement>;
  @ViewChild("contentEl", { static: false }) contentEl: ElementRef<HTMLElement>;
  @ViewChild("descriptionEl", { static: false })
  descriptionEl: ElementRef<HTMLElement>;

  private alive$ = new Subject();

  private static readonly panelNamePrefix = "boardHeroDemoFormPanel";
  private static readonly inputFieldPrefix = `${BoardHeroDemoFormPanelComponent.panelNamePrefix}.InputField`;
  private static readonly errorMessagePrefix = `${BoardHeroDemoFormPanelComponent.panelNamePrefix}.ErrorMessage`;
  private static readonly successMessagePrefix = `${BoardHeroDemoFormPanelComponent.panelNamePrefix}.SuccessMessage`;

  formText = {
    name: this.translateService.instant(
      `${BoardHeroDemoFormPanelComponent.inputFieldPrefix}.Name`,
    ),
    email: this.translateService.instant(
      `${BoardHeroDemoFormPanelComponent.inputFieldPrefix}.Email`,
    ),
    phone: this.translateService.instant(
      `${BoardHeroDemoFormPanelComponent.inputFieldPrefix}.Phone`,
    ),
    company: this.translateService.instant(
      `${BoardHeroDemoFormPanelComponent.inputFieldPrefix}.Company`,
    ),
  };
  errMsg: string = null;
  succMsg: string = null;
  captchaResponse: string = null;
  requestSuccess: boolean = false;
  demoGroup: FormGroup;
  buttonType = ButtonType;
  isModalShow: boolean = false;
  videoData: IModalVideo;
  media: IVideoPickerData;
  processedDescription: string = "";
  processedTitle: string = "";

  buttons: IBoardHeroDemoFormButtonItemViewModel[] = [];
  buttonsAsLink: IBoardHeroDemoFormButtonItemViewModel[] = [];
  curveTypes: CurveType = "None";

  constructor(
    private http: HttpClient,
    private hostRef: ElementRef,
    private translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.prepareMedia();
    this.demoGroup = new FormGroup({
      name: new FormControl("", Validators.required),
      email: new FormControl(
        "",
        Validators.compose([
          Validators.required,
          Validators.email,
          Validators.pattern(
            "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
          ),
        ]),
      ),
      phone: new FormControl("", Validators.required),
      company: new FormControl(""),
      privacyPolicy: new FormControl(false, Validators.requiredTrue),
    });

    this.separateButtonsByTypes();
    this.initProcessedText();
  }

  reCaptchaValidated(response: string) {
    this.captchaResponse = response;
  }

  resetCaptcha() {
    this.captcha.reset();
    this.captchaResponse = null;
  }

  ngAfterViewInit(): void {
    this.setCustomThemeProperties(this.data);
    applyTitleSettings(this.data, this.titleEl);
    requestAnimationFrame(() => {
      this.processTexts();
    });
  }

  private setCustomThemeProperties(d: IBoardHeroDemoFormPanelData) {
    applyRteSettings(this.data, this.descriptionEl);
    if (d.overlayColor && d.overlayColor.value) {
      this.hostRef.nativeElement.style.setProperty(
        "--hero-overlay-color",
        d.overlayColor.value,
      );
    }
    if (
      d.overlayPercent &&
      d.overlayPercent.value &&
      d.overlayPercent.value > 0
    ) {
      this.hostRef.nativeElement.style.setProperty(
        "--hero-overlay-percent",
        d.overlayPercent.value / 100,
      );
    }
    if (d.buttonsColor && d.buttonsColor.value) {
      this.hostRef.nativeElement.style.setProperty(
        "--hero-buttons-color",
        d.buttonsColor.value,
      );
    }

    this.curveTypes = d.curve.value;
  }

  private prepareMedia() {
    if (
      this.data.backgroundMedia.value &&
      this.data.backgroundMedia.value.video
    ) {
      this.prepareBackgroundVideo(this.data.backgroundMedia.value.video);
    }

    if (
      this.data.backgroundMedia.value &&
      this.data.backgroundMedia.value.image
    ) {
      this.panelEl.nativeElement.style.setProperty(
        "--board-hero-demo-form-background-image",
        `url(${this.data.backgroundMedia.value.image.src})`,
      );
    }
  }

  private prepareBackgroundVideo(video: IVideoPickerData): void {
    const { desktop, mobile } = video;

    this.media = {
      desktop: desktop || mobile,
      mobile: mobile || desktop,
    };
  }

  sendRequest(event) {
    event.preventDefault();
    this.errMsg = "";

    if (!this.demoGroup.invalid && this.captchaResponse) {
      const data = { ...this.demoGroup.value };
      data.mailType = this.data.mailType.value;
      data.reCaptchaToken = this.captchaResponse;
      this.http.post("ubaseline/api/demoFormPanel/sendRequest", data).subscribe(
        (data: IBoardHeroDemoFormPanelResponseData) => {
          if (data.reCaptchaFailed) {
            this.errMsg = this.translateService.instant("reCaptcha.Error");
            this.resetCaptcha();
            return;
          }

          this.demoGroup.reset({
            name: "",
            email: "",
            phone: "",
            company: "",
            privacyPolicy: false,
          });

          this.demoGroup.markAsUntouched();
          this.demoGroup.markAsPristine();
          this.successOnSend();
        },
        (error) => console.error("An error has occured", error),
      );
    } else {
      if (!this.demoGroup.value.privacyPolicy) {
        this.errMsg = this.translateService.instant(
          `${BoardHeroDemoFormPanelComponent.errorMessagePrefix}.PrivacyPolicy`,
        );
      }
      if (!this.captchaResponse) {
        this.errMsg = this.translateService.instant("reCaptcha.CheckRecaptcha");
      } else if (this.demoGroup.controls.name.status === "INVALID") {
        this.errMsg = this.translateService.instant(
          `${BoardHeroDemoFormPanelComponent.errorMessagePrefix}.MissingName`,
        );
      } else if (this.demoGroup.controls.email.status === "INVALID") {
        this.errMsg = this.translateService.instant(
          `${BoardHeroDemoFormPanelComponent.errorMessagePrefix}.Email`,
        );
      } else if (this.demoGroup.controls.phone.status === "INVALID") {
        this.errMsg = this.translateService.instant(
          `${BoardHeroDemoFormPanelComponent.errorMessagePrefix}.MissingPhone`,
        );
      }
    }
  }
  successOnSend() {
    this.requestSuccess = true;
    this.succMsg = this.translateService.instant(
      `${BoardHeroDemoFormPanelComponent.successMessagePrefix}.OnSend`,
    );
    this.resetCaptcha();
    this.startTimer();
  }

  startTimer() {
    let countDown;
    let timeLeft: number = 5;
    const source = timer(1000, 1000);
    countDown = source.pipe(takeUntil(this.alive$)).subscribe((val) => {
      timeLeft--;
      if (timeLeft === 0) {
        this.requestSuccess = false;
        countDown.unsubscribe();
      }
    });
  }

  validateFormField(fieldName: string): string {
    if (
      this.demoGroup.controls[fieldName].valid &&
      this.demoGroup.controls[fieldName].touched
    ) {
      return "valid";
    }
    if (
      this.demoGroup.controls[fieldName].invalid &&
      this.demoGroup.controls[fieldName].touched
    ) {
      return "invalid";
    }
  }

  openModalVideo(index: number) {
    this.buttons[index].isModalShow = true;
  }

  closeModalVideo(index: number) {
    this.buttons[index].isModalShow = false;
  }

  prepareVideoData(button: IButton): IModalVideo {
    return <IModalVideo>{
      title: null,
      description: null,
      params: `?controls=1&muted=0&autoplay=1&showinfo=0`,
      video: {
        url: button.url,
        type: this.resolveVideoType(button.type),
      },
    };
  }

  private separateButtonsByTypes() {
    if (!this.data.buttons.value) return;

    const { value } = this.data.buttons;

    this.buttons = value.filter((button) => !button.renderAsLink.value);
    this.buttonsAsLink = value.filter((button) => button.renderAsLink.value);
  }

  private resolveVideoType(type: number): number {
    return type === ButtonType.media ? 2 : 3; // equals or not to UMEDIA_TYPE in modal video component
  }

  isButtonMediaInternalVideo(button: IButton) {
    return button.url.match(/(\.(mp4|mov|mpeg))$/i);
  }

  ngOnDestroy() {
    this.alive$.next();
    this.alive$.complete();
  }

  private processTexts(): void {
    const rotatingWordsData =
      this.data.rotatingWords && this.data.rotatingWords.value
        ? this.data.rotatingWords.value
        : [];

    this.processedDescription = processContent(
      this.processedDescription,
      rotatingWordsData,
    );
    this.processedTitle = processContent(
      this.processedTitle,
      rotatingWordsData,
      window.getComputedStyle(this.titleEl.nativeElement).fontSize,
    );
  }

  private initProcessedText(): void {
    const rawDescription =
      this.data.description && this.data.description.value
        ? this.data.description.value
        : "";
    const rawTitle =
      this.data.title && this.data.title.value ? this.data.title.value : "";

    this.processedDescription = rawDescription;
    this.processedTitle = rawTitle;
  }
}
